/**
 * Typed loaders for the data registers shipped alongside this package:
 *
 *   - `data/source-licenses.json` — per-source attribution + license metadata
 *   - `data/population-denominators.json` — per-region population for per-capita
 *     rate normalisation
 *
 * Both are JSON files (so they're editable without a build step and
 * inspectable by humans); this module gives them a strongly-typed runtime
 * surface for consumers.
 *
 * Browser-safe: imports are plain JSON, no `node:*` touched. Safe to call
 * from PaCC's browser bundles (e.g. the eventual Phase 6 briefing-export
 * surface, which needs the per-source attribution text).
 *
 * The schemas are at `data/*.schema.json` next to each JSON file; the
 * shapes here mirror those schemas. Drift between the schema + this file
 * should be flagged in code review.
 */

import licensesRaw from '../data/source-licenses.json';
import populationRaw from '../data/population-denominators.json';
import coverageTierRaw from '../data/coverage-tier.json';
import type { PopulationDenominator } from './index.js';

// ---------------------------------------------------------------------------
// Types — mirror the JSON schemas. PopulationEntry is the GOVERNANCE-rich
// shape (carries verifyUrl + verifiedAt + provisional + notes); the engine's
// existing PopulationDenominator (in index.ts) is the lean per-call shape
// resolveDenominator() consumes. Use `toPopulationDenominators()` to convert.
// ---------------------------------------------------------------------------

export interface LicenseEntry {
  publisher: string;
  publisherUrl: string;
  license: string;
  redistributable: boolean | null;
  commercialUse: boolean | null;
  derivativeWorks: boolean | null;
  attributionText: string;
  verifyUrl: string;
  licenseVerified: boolean;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  operatorWarnings: string[];
}

/** A country that does not publish official routine surveillance data.
 *  Surfaced in §7.22 Feed Health "Sovereignty" tab so operators briefing on
 *  these regions explicitly flag the dark spot rather than treat
 *  absence-of-signal as evidence-of-absence. */
export interface NonDisseminator {
  iso3: string;
  name: string;
  region: string;
  rationale: string;
  indirectInferenceSources: string[];
  operatorAction?: string;
}

/** A parallel open-data fallback adapter that PARTIALLY closes a blocked
 *  adapter's gap on different infrastructure (CD R11 #6). A jurisdiction is
 *  rarely all-or-nothing — QLD's PDF feed is Cloudflare-blocked but its
 *  open-data CKAN portal carries the COVID stream. `scope` names what the
 *  fallback covers ("COVID-only") so the §7.22 Sovereignty tab can render a
 *  ◐ PARTIAL badge instead of a misleading all-LIVE or all-BLOCKED. */
export interface ParallelFallback {
  id: string;
  rationale: string;
  /** Short human label for what the fallback covers, e.g. "COVID-only". */
  scope: string;
  license: string;
}

/** A wire-ready adapter shipping with structural `blockedReason` — DIFFERENT
 *  from non-disseminator (where no adapter can exist). Operator action is
 *  compliance / allowlist / MoU, NOT "no source exists". */
export interface BlockedAdapterEntry {
  id: string;
  blockedReason: 'sanctions' | 'antibot' | 'partnership' | 'geo';
  unblockPath: string;
  operatorAction: string;
  /** Optional partial-coverage fallback on different infra (CD R11 #6). */
  parallelFallback?: ParallelFallback;
}

/** An adapter whose source format yields zero rows during quiet periods by
 *  design (e.g. monthly outbreak-alert bulletins). Helps operators
 *  distinguish "no outbreaks announced" from "no diseases / silent failure". */
export interface LowYieldByDesignEntry {
  id: string;
  rationale: string;
  operatorAction: string;
}

/** A wire-ready adapter whose LIVE fetch is deferred pending URL / partner
 *  confirmation (fixture-tested; fetcher throws by design). STAGED, not down,
 *  and not a Cloudflare/sanctions gate-block. A staged country is NOT dark —
 *  `interimCoverage` names the live multilateral source covering it meanwhile. */
export interface StagedAdapterEntry {
  id: string;
  reason: string;
  interimCoverage: string;
  /** Set when the adapter WAS live but its upstream is no longer publicly
   *  machine-accessible (SPA-only, hard-403, retired dataset, geo/DNS-firewalled
   *  egress incl. CI). DIFFERENT from the URL/partner-pending staged kind (where
   *  the fetcher throws by design). Honestly STAGED, not "broken": the data left
   *  the open web; the adapter code is sound. */
  sourceLayerBlocked?: boolean;
  /** Concrete path back to live (re-target a replacement endpoint, partner/
   *  allowlist ask, or a resident-egress relay). Present on source-layer-blocked
   *  entries so operators see "what unblocks this", not just "it's down". */
  unblockPath?: string;
}

export interface CoverageTierRegister {
  lastReviewed: string;
  tiers: Record<string, string>;
  nonDisseminators: readonly NonDisseminator[];
  blockedAdapters: readonly BlockedAdapterEntry[];
  lowYieldByDesign: readonly LowYieldByDesignEntry[];
  stagedAdapters?: readonly StagedAdapterEntry[];
}

export interface PopulationEntry {
  name: string;
  population: number | null;
  populationAsOf?: string | null;
  populationCi?: { low: number; high: number } | null;
  verifyUrl: string;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  provisional?: boolean;
  notes?: string;
}

// ---------------------------------------------------------------------------
// Frozen-at-load views — JSON is static at build time; freezing prevents
// downstream code from mutating the shared register.
// ---------------------------------------------------------------------------

const LICENSES = Object.freeze(
  (licensesRaw as { sources: Record<string, LicenseEntry> }).sources,
) as Readonly<Record<string, LicenseEntry>>;

const COVERAGE_TIER = Object.freeze(coverageTierRaw as unknown as CoverageTierRegister);

const POPULATION = Object.freeze(
  Object.fromEntries(
    Object.entries((populationRaw as { regions: Record<string, unknown> }).regions)
      // The register file uses `_TODO_*` keys for free-form placeholder blocks
      // (regions not yet seeded). Filter them out — they don't conform to
      // PopulationEntry shape and downstream consumers shouldn't see them.
      .filter(([k]) => !k.startsWith('_')),
  ),
) as Readonly<Record<string, PopulationEntry>>;

// ---------------------------------------------------------------------------
// License register API
// ---------------------------------------------------------------------------

/** Return all license entries keyed by source id. */
export function loadSourceLicenses(): Readonly<Record<string, LicenseEntry>> {
  return LICENSES;
}

/** Lookup one license entry by source id. Returns undefined when missing —
 *  callers shouldn't render an attribution they don't have, and shouldn't
 *  guess. The license-register CI gate (tests/license-register.test.ts)
 *  ensures every wired adapter has an entry. */
export function getLicense(sourceId: string): LicenseEntry | undefined {
  return LICENSES[sourceId];
}

// ---------------------------------------------------------------------------
// Coverage-tier register API (sovereignty / blocked / low-yield-by-design)
// ---------------------------------------------------------------------------

/** Load the full coverage-tier register — drives §7.22 Feed Health
 *  Sovereignty tab. Browser-safe (frozen JSON, no node:* imports). */
export function loadCoverageTier(): CoverageTierRegister {
  return COVERAGE_TIER;
}

/** Quick accessor: countries that do not publish official surveillance data. */
export function listNonDisseminators(): readonly NonDisseminator[] {
  return COVERAGE_TIER.nonDisseminators;
}

/** Quick accessor: wire-ready adapters shipping with structural blockedReason
 *  + each one's unblock path + operator action. */
export function listBlockedAdapters(): readonly BlockedAdapterEntry[] {
  return COVERAGE_TIER.blockedAdapters;
}

/** Lookup the blocked-adapter entry for one adapter id, when one exists.
 *  Useful for surfaces that want to render "BLOCKED — partnership-required"
 *  with the unblock-path text inline rather than maintain a duplicate copy. */
export function getBlockedAdapter(adapterId: string): BlockedAdapterEntry | undefined {
  return COVERAGE_TIER.blockedAdapters.find(b => b.id === adapterId);
}

/** Quick accessor: low-yield-by-design adapters (e.g. NT monthly outbreak
 *  bulletins → quiet months yield zero rows). Used to suppress noisy
 *  "source produced no signals" alerts on adapters where that's expected. */
export function listLowYieldByDesign(): readonly LowYieldByDesignEntry[] {
  return COVERAGE_TIER.lowYieldByDesign;
}

/** Wire-ready adapters whose live fetch is deferred (URL/partner pending) +
 *  the live multilateral source covering each meanwhile. */
export function listStagedAdapters(): readonly StagedAdapterEntry[] {
  return COVERAGE_TIER.stagedAdapters ?? [];
}

/** The set of source ids the liveness harness should treat as STAGED (not
 *  down) — gate-blocked OR wire-ready-by-design. Pass to summariseLiveness so
 *  a deferred national feed never reads as a fault. */
export function stagedSourceIds(): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const b of COVERAGE_TIER.blockedAdapters) ids.add(b.id);
  for (const s of COVERAGE_TIER.stagedAdapters ?? []) ids.add(s.id);
  return ids;
}

/** Sources with operator-visible caveats (e.g. "no commercial reuse",
 *  "no implication of WHO endorsement"). Phase 6 briefing-export must
 *  surface these in the briefing's data-limitations appendix. */
export function getSourcesWithWarnings(): Array<{ id: string; entry: LicenseEntry }> {
  const out: Array<{ id: string; entry: LicenseEntry }> = [];
  for (const [id, entry] of Object.entries(LICENSES)) {
    if (entry.operatorWarnings.length > 0) out.push({ id, entry });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Population register API
// ---------------------------------------------------------------------------

/** Return all population entries keyed by region key (e.g. 'AUS', 'AU-NSW'). */
export function loadPopulationDenominators(): Readonly<Record<string, PopulationEntry>> {
  return POPULATION;
}

/** Lookup one population entry by region key. Returns undefined when the
 *  entry doesn't exist OR when the entry exists but its population is null
 *  (seeded-but-unverified). Both states mean "do not normalise rates against
 *  this denominator." */
export function getPopulation(regionKey: string): PopulationEntry | undefined {
  const entry = POPULATION[regionKey];
  if (!entry) return undefined;
  if (entry.population == null) return undefined;
  return entry;
}

/** Convert the governance-rich register into the engine's lean
 *  PopulationDenominator shape (consumed by `resolveDenominator()` in
 *  index.ts). ONLY entries with verified populations are included —
 *  unverified entries would silently produce wrong per-capita rates.
 *
 *  Currently doesn't carry iso3 or centroidLatLon (the register schema
 *  doesn't have these yet). Engine matching falls back to name-equality
 *  for now; iso3/centroid wiring is a future schema extension when the
 *  register has been substantially verified. */
export function toPopulationDenominators(): PopulationDenominator[] {
  const out: PopulationDenominator[] = [];
  for (const [, entry] of Object.entries(POPULATION)) {
    if (entry.population == null || entry.population <= 0) continue;
    out.push({ name: entry.name, population: entry.population });
  }
  return out;
}
